import { toSignal } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideDownload,
  lucideRotateCcw,
  lucideChevronRight,
  lucideCircleCheck,
  lucideCircleX,
  lucideClock,
  lucideImage,
  lucideMonitor,
  lucideChevronDown,
} from '@ng-icons/lucide';
import { HlmTabsImports } from '@spartan-ng/helm/tabs';
import { ConfirmationDialogService } from '@shared/services/confirmation-dialog.service';
import { BrnDialogImports } from '@spartan-ng/brain/dialog';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { SkeletonLoader } from '@shared/components/skeleton-loader/skeleton-loader';
import { StatePanel } from '@shared/components/state-panel/state-panel';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { AnalysisDetailStore } from '@features/analyses/store/analysis-detail.store';
import { ToastService } from '@shared/services/toast.service';
import {
  AnalysisPlanning,
  BusinessStatus,
  FileOverviewItem,
  getTechnicalStatusLabel,
  normalizeTechnicalState,
} from '@shared/models/analysis.model';
import { AnalysisViewer, ViewerSelection } from '@shared/components/analysis-viewer/analysis-viewer';
import { AnalysisPlanningResultsView } from '@shared/components/analysis-planning-results-view/analysis-planning-results-view';
import { AnalysisStatusSelector } from '@shared/components/analysis-status-selector/analysis-status-selector';
import { AnalysisDetailAddressDetails } from '@features/analyses/components/analysis-detail-address-details/analysis-detail-address-details';
import { AnalysisDetailDebugPanel } from '@features/analyses/components/analysis-detail-debug-panel/analysis-detail-debug-panel';
import { AnalysisTimeline } from '@features/analyses/components/analysis-timeline/analysis-timeline';
import { AnalysisCustomerTab } from '@features/analyses/components/analysis-customer-tab/analysis-customer-tab';
import { AnalysisFilesTab } from '@features/analyses/components/analysis-files-tab/analysis-files-tab';
import {
  AnalysisWorkflowFacade,
  type PipelineActionExecution,
} from '@features/analyses/services/analysis-workflow.facade';
import { BackgroundExecutionService } from '@core/services/background-execution.service';
import { AnalysisUpdateFacade } from '@features/analyses/services/analysis-update.facade';
import { environment } from '@env/environment';
import { PageHeaderMetaService } from '@shared/services/page-header-meta.service';
import { PageTabsService } from '@shared/services/page-tabs.service';
import { CampaignsService } from '@features/campaigns/services/campaigns.service';
import { LAST_PIPELINE_TASK } from '@features/analyses/config/pipeline-stage.config';
import { getAnalysisErrorMessage } from '@shared/models/error-code.model';

const debugLog = (...args: unknown[]) => {
  if (!environment.production) {
    console.log(...args);
  }
};

type DetailTab = 'overview' | 'roof' | 'planning' | 'files' | 'customer';
type ProgressCardTab = 'progress' | 'details';

@Component({
  selector: 'app-analysis-detail',
  standalone: true,
  imports: [
    CommonModule,
    NgIconComponent,
    HlmTabsImports,
    ...BrnDialogImports,
    ...HlmDialogImports,
    SkeletonLoader,
    StatePanel,
    AnalysisViewer,
    AnalysisPlanningResultsView,
    AnalysisStatusSelector,
    AnalysisDetailAddressDetails,
    AnalysisDetailDebugPanel,
    AnalysisTimeline,
    AnalysisCustomerTab,
    AnalysisFilesTab,
  ],
  providers: [
    AnalysisWorkflowFacade,
    AnalysisUpdateFacade,
    provideIcons({
      lucideDownload,
      lucideRotateCcw,
      lucideChevronRight,
      lucideCircleCheck,
      lucideCircleX,
      lucideClock,
      lucideImage,
      lucideMonitor,
      lucideChevronDown,
    }),
  ],
  templateUrl: './analysis-detail.html',
  styleUrl: './analysis-detail.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisDetail implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  readonly currentOrgStore = inject(CurrentOrganizationStore);
  private readonly analysisDetailStore = inject(AnalysisDetailStore);
  private readonly toastService = inject(ToastService);
  private readonly backgroundExecution = inject(BackgroundExecutionService);
  private readonly pageHeaderMeta = inject(PageHeaderMetaService);
  private readonly pageTabs = inject(PageTabsService);
  private readonly campaignsService = inject(CampaignsService);
  readonly workflowFacade = inject(AnalysisWorkflowFacade);
  readonly updateFacade = inject(AnalysisUpdateFacade);

  // ─── UI state ─────────────────────────────────────────────────────────────

  activeTab = signal<DetailTab>('overview');
  progressCardTab = signal<ProgressCardTab>('progress');
  executionExpanded = signal(true);
  addressDialogOpen = signal(false);
  viewerDialogState = signal<'open' | 'closed'>('closed');
  selectedPlanningId = signal<string | null>(null);
  private readonly planningLink = toSignal(this.route.queryParamMap, {initialValue: this.route.snapshot.queryParamMap});
  private appliedPlanningLink: string | null = null;
  campaignTitle = signal<string | null>(null);
  private campaignLookupKey: string | null = null;
  private readonly headerControlsTemplate = signal<TemplateRef<unknown> | null>(null);

  @ViewChild('analysisHeaderControls', { static: true })
  set analysisHeaderControls(template: TemplateRef<unknown>) {
    this.headerControlsTemplate.set(template);
  }
  // Delegated signals exposed for template binding
  isSyncing = computed(() => this.workflowFacade.isSyncing());
  isPipelineActionRunning = computed(() => this.workflowFacade.isPipelineActionRunning());
  isSavingMarkerPosition = computed(() => this.updateFacade.isSavingMarkerPosition());

  // ─── Store selectors ──────────────────────────────────────────────────────

  analysis = this.analysisDetailStore.selectDetail;
  analysisTitle = computed(() => {
    const analysis = this.analysis();
    if (!analysis) return '';
    if (analysis.address) return `${analysis.address.street} ${analysis.address.houseNumber}, ${analysis.address.postalCode} ${analysis.address.city}`;
    if (analysis.title) return analysis.title;
    const customTitle = (analysis.metadata as Record<string, unknown> | undefined)?.['title'];
    if (typeof customTitle === 'string' && customTitle) return customTitle;
    return `Analyse ${analysis.analysisId.slice(-8)}`;
  });
  coordinatesLabel = computed(() => {
    const address = this.analysis()?.address;
    if (address?.latitude == null || address.longitude == null) return '';
    return `${address.latitude.toFixed(6)} · ${address.longitude.toFixed(6)}`;
  });
  polygons = this.analysisDetailStore.selectPolygons;
  plannings = this.analysisDetailStore.selectPlannings;
  primaryPlanningId = computed(() => {
    const plans = this.plannings() ?? [];
    const saved = this.analysis()?.primaryPlanningId;
    if (saved && plans.some(plan => plan.id === saved)) return saved;
    return [...plans].sort((a, b) => Number(b.status === 'completed') - Number(a.status === 'completed')
      || String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''))
      || a.id.localeCompare(b.id))[0]?.id ?? null;
  });

  // ─── Planning tab gating ──────────────────────────────────────────────────
  // Task 4 = max_placement, task 5 = planning (see analysis-detail-debug-panel).
  // The "Planung" tab only shows real content once both tasks have completed —
  // otherwise it is disabled (not reached yet) or shows the pipeline error.
  planningTaskFailed = computed(() => {
    const ts = this.analysis()?.technicalStatus;
    if (!ts) return false;
    return (ts.taskKey === 'max_placement' || ts.taskKey === 'planning') && ts.state === 'failed';
  });

  planningFailureDescription = computed(() => {
    const status = this.analysis()?.technicalStatus;
    const detail = status?.errorDetail || status?.errorMessage;
    const reason = detail?.trim().toLowerCase() === 'planung fehlgeschlagen'
      ? 'Die Maximalbelegung oder Planung konnte nicht berechnet werden.'
      : detail || 'Die Maximalbelegung oder Planung konnte nicht berechnet werden.';
    return `${reason} Starten Sie die Planungsstufe über die Ausführungsleiste erneut.`;
  });

  planningTaskReady = computed(() => {
    const ts = this.analysis()?.technicalStatus;
    if (!ts) return false;
    if (ts.stage === 'complete') return true;
    const task = ts.task ?? 0;
    if (task > 5) return true;
    return task === 5 && ts.state === 'completed';
  });

  planningTabDisabled = computed(
    () =>
      this.plannings().length === 0 &&
      !this.planningTaskFailed() &&
      !this.planningTaskReady(),
  );
  filesOverview = this.analysisDetailStore.selectFilesOverview;
  lastSyncAt = this.analysisDetailStore.selectLastSyncAt;
  isPolling = this.analysisDetailStore.selectPolling;
  error = this.analysisDetailStore.error;

  private readonly routeParams = toSignal(this.route.paramMap, {initialValue: this.route.snapshot.paramMap});
  private readonly analysisId = computed(() => this.routeParams().get('analysisId'));
  private loadedAnalysisId: string | null = null;

  readonly organizationId = computed(
    () => this.currentOrgStore.organization()?.id || null,
  );

  isLoading = computed(
    () => this.analysisDetailStore.selectLoadingStates().any,
  );

  isLoadingFiles = computed(
    () => this.analysisDetailStore.selectLoadingStates().files,
  );

  isLoadingPlannings = computed(
    () => this.analysisDetailStore.selectLoadingStates().plannings,
  );

  hasFiles = computed(() => {
    const files = this.filesOverview() || [];
    return files.filter((file: FileOverviewItem) => file.exists).length > 0;
  });

  executionSummary = computed(() => {
    const rawStatus = this.analysis()?.technicalStatus;
    if (!rawStatus) {
      return {
        title: 'Bereit zur Ausführung',
        subtitle: 'Noch keine Aufgabe ausgeführt',
        dotClass: 'bg-muted-foreground/50',
      };
    }

    const status = rawStatus.task > LAST_PIPELINE_TASK
      ? {
          ...rawStatus,
          task: LAST_PIPELINE_TASK,
          totalTasks: LAST_PIPELINE_TASK,
          taskKey: 'planning' as const,
          stage: 'complete' as const,
          state: 'completed' as const,
          errorCode: undefined,
          errorMessage: undefined,
          errorDetail: undefined,
        }
      : rawStatus;
    const state = normalizeTechnicalState(status.state);
    const title = getTechnicalStatusLabel({ ...status, state });
    const detail = status.task > 0
      ? `Aufgabe ${Math.min(status.task, LAST_PIPELINE_TASK)} von ${LAST_PIPELINE_TASK}`
      : 'Noch keine Aufgabe ausgeführt';

    if (state === 'failed') {
      return {
        title,
        subtitle: getAnalysisErrorMessage(status.errorCode, status.errorMessage, status.errorDetail) || detail,
        dotClass: 'bg-destructive',
      };
    }
    if (
      state === 'completed' &&
      status.task >= LAST_PIPELINE_TASK
    ) {
      return {
        title,
        subtitle: detail,
        dotClass: 'bg-success',
      };
    }
    if (state === 'processing' || state === 'queued') {
      return {
        title,
        subtitle: detail,
        dotClass: 'bg-primary animate-pulse',
      };
    }
    return {
      title,
      subtitle: detail,
      dotClass: state === 'completed' ? 'bg-success' : 'bg-primary',
    };
  });

  runtimeData = computed<Record<string, unknown>>(() => {
    const meta = this.analysis()?.metadata ?? {};
    const si = meta['si'] as Record<string, unknown> | undefined;
    return (si?.['runtime'] as Record<string, unknown>) ?? {};
  });

  // SI-level source data extracted from the analysis metadata.
  // Used by the debug panel to show raw Solar Insight source fields.
  sourceData = computed<Record<string, unknown> | null>(() => {
    const meta = this.analysis()?.metadata ?? {};
    const si = meta['si'] as Record<string, unknown> | undefined;
    return (si?.['sourceData'] ?? meta['sourceData'] ?? null) as Record<string, unknown> | null;
  });

  constructor() {
    // A finished analysis opens on its result: the pipeline stays one line until asked for.
    // Running or failed analyses open it, because then the steps are the news.
    let pipelineSetFor: string | null = null;
    effect(() => {
      const analysis = this.analysis();
      if (!analysis || analysis.analysisId === pipelineSetFor) return;
      pipelineSetFor = analysis.analysisId;
      const status = analysis.technicalStatus;
      const finished = !!status && normalizeTechnicalState(status.state) === 'completed' && status.task >= LAST_PIPELINE_TASK;
      this.executionExpanded.set(!finished);
    });
    effect(() => {
      const id = this.analysisId();
      const organizationId = this.organizationId();
      if (!id || !organizationId) return;
      untracked(() => {
        if (this.loadedAnalysisId) this.backgroundExecution.handOffAnalysisToBackground(this.loadedAnalysisId);
        this.loadedAnalysisId = id;
        this.analysisDetailStore.clearCurrentAnalysis();
        this.appliedPlanningLink = null;
        this.selectedPlanningId.set(null);
        this.activeTab.set('overview');
        this.loadAnalysis();
      });
    });
    effect(() =>
      this.pageTabs.set(
        [
          { value: 'overview', label: 'Übersicht' },
          {
            value: 'roof',
            label: 'Dachanalyse',
          },
          {
            value: 'planning',
            label: 'Planung',
            disabled: this.planningTabDisabled(),
            disabledReason: this.planningTabDisabled()
              ? 'Verfügbar, sobald die Planungsstufe erreicht ist'
              : undefined,
          },
          { value: 'customer', label: 'Vertrieb' },
          {
            value: 'files',
            label: 'Dateien',
            count:
              (this.filesOverview() || []).filter((file) => file.exists).length ||
              undefined,
          },
        ],
        this.activeTab(),
        (tab) => this.selectTab(tab as DetailTab),
      ),
    );
    effect(() => {
      void this.loadCampaignTitle(
        this.organizationId(),
        this.analysis()?.campaignId ?? null,
      );
    });
    effect(() => {
      const analysis = this.analysis();
      if (!analysis) return this.pageHeaderMeta.set(null);
      this.pageHeaderMeta.set({
        title: this.analysisTitle(),
        breadcrumbLabel: analysis.analysisId.slice(-8),
        titleActions: analysis.address
          ? [
              ...(this.currentOrgStore.can('address', 'update') ? [{
                label: 'Adresse bearbeiten',
                icon: 'lucidePencil',
                ariaLabel: 'Adresse bearbeiten',
                onClick: () => this.addressDialogOpen.set(true),
              }] : []),
              {
                label: 'In Google Maps öffnen',
                icon: 'lucideExternalLink',
                ariaLabel: 'Adresse in Google Maps öffnen',
                onClick: () => this.openAddressInMaps(),
              },
            ]
          : [],
        compactBadges: false,
        actionsTemplate: this.headerControlsTemplate(),
        badges: [
          { label: 'Erstellt', value: this.formatHeaderDate(analysis.createdAt) },
          ...(analysis.analysisType
            ? [{ label: 'Typ', value: `${analysis.analysisType.toUpperCase()}-Analyse` }]
            : []),
          ...(analysis.source
            ? [{ label: 'Quelle', value: this.sourceLabel(analysis.source) }]
            : []),
          ...(analysis.campaignId
            ? [{
                label: 'Kampagne',
                value: this.campaignTitle() ?? undefined,
                href: this.campaignHref(analysis.campaignId),
              }]
            : []),
          // No status chip: the pipeline line right below the tabs already says it.
        ],
      });
    });
    effect(() => {
      const items = (this.plannings() || []) as AnalysisPlanning[];
      const selectedId = this.selectedPlanningId();
      const linkedId = this.planningLink().get('planningId');
      if (linkedId !== this.appliedPlanningLink && items.some(p => p.id === linkedId)) {
        this.appliedPlanningLink = linkedId; this.selectedPlanningId.set(linkedId); this.selectTab('planning'); return;
      }
      if (!linkedId) this.appliedPlanningLink = null;

      if (!items.length) {
        if (selectedId !== null) {
          this.selectedPlanningId.set(null);
        }
        return;
      }

      if (
        !selectedId ||
        !items.some((planning) => planning.id === selectedId)
      ) {
        const preferred =
          items.find((planning) => planning.id === this.route.snapshot.queryParamMap.get('planningId')) ??
          items.find((planning) => planning.status === 'running') ??
          items.find((planning) => planning.id === this.primaryPlanningId()) ??
          items.find((planning) => planning.status === 'completed') ??
          items[0];
        this.selectedPlanningId.set(preferred.id);
      }
    });
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  private readonly handleVisibilityChange = () => {
    this.analysisDetailStore.setPageVisible(!document.hidden);
  };

  ngOnInit(): void {
    this.analysisDetailStore.setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  ngOnDestroy(): void {
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );
    const analysisId = this.analysisId();
    this.analysisDetailStore.clearCurrentAnalysis();
    // Hand the analysis off to the background tracker so SSE continues after
    // navigation and the user receives a notification on completion.
    if (analysisId) {
      this.backgroundExecution.handOffAnalysisToBackground(analysisId);
    }
  }

  // ─── Navigation ───────────────────────────────────────────────────────────

  selectTab(tab: DetailTab): void {
    this.activeTab.set(tab);

    // Load polygons whenever the roof tab is opened.
    // forceRefresh bypasses the client-side cache so changes made elsewhere
    // (e.g. campaign review) are always reflected when returning to this tab.
    if (tab === 'roof') {
      const orgId = this.organizationId();
      const analysisId = this.analysisId();
      if (orgId && analysisId) {
        this.analysisDetailStore.loadPolygons({
          organizationId: orgId,
          analysisId,
          forceRefresh: true,
        });
      }
    }

    // Lazy-load plannings on first planning tab visit.
    // This keeps initial detail payload light and ensures planning data is
    // fetched even before full pipeline completion.
    if (tab === 'planning' && this.plannings() === null) {
      const orgId = this.organizationId();
      const analysisId = this.analysisId();
      if (orgId && analysisId) {
        this.analysisDetailStore.loadPlannings({
          organizationId: orgId,
          analysisId,
        });
      }
    }
  }

  selectProgressCardTab(tab: ProgressCardTab): void {
    this.progressCardTab.set(tab);
  }

  toggleExecutionExpanded(): void {
    this.executionExpanded.update((expanded) => !expanded);
  }

  readonly expandedViewerSelection = signal<ViewerSelection | null>(null);

  openViewerDialog(selection: ViewerSelection): void {
    this.expandedViewerSelection.set(selection);
    this.viewerDialogState.set('open');
  }

  closeViewerDialog(): void {
    this.viewerDialogState.set('closed');
  }

  // ─── Delegated actions ────────────────────────────────────────────────────

  syncAnalysis(): Promise<void> {
    return this.workflowFacade.syncAnalysis();
  }

  resetStuckState(): Promise<void> {
    return this.workflowFacade.resetStuckState();
  }

  onTimelineActionExecuted(
    actionPayload: PipelineActionExecution,
  ): Promise<void> {
    return this.workflowFacade.onTimelineActionExecuted(actionPayload);
  }

  startRoofDetection(): Promise<void> {
    return this.onTimelineActionExecuted({
      action: {
        action: 'start-analysis',
        targetStatus: 'analyzing',
        label: 'Erkennung starten',
        hasPermission: true,
      },
      taskNo: 2,
      startTask: 2,
      endTask: 3,
      trigger: 'start',
    });
  }

  onStatusChanged(newStatus: string): Promise<void> {
    return this.updateFacade.onStatusChanged(newStatus as BusinessStatus);
  }

  onDetailsUpdated(values: {
    powerConsumption: number;
    electricityPrice: number;
    addressData: {
      roofType?: 'flat' | 'normal' | 'both' | null;
      isApartmentBuilding?: boolean | null;
      hasSolar?: boolean | null;
    };
  }): Promise<void> {
    return this.updateFacade.onDetailsUpdated(values);
  }

  onHasSolarChanged(hasSolar: boolean): Promise<void> {
    return this.updateFacade.onHasSolarChanged(hasSolar);
  }

  openAddressInMaps(): void {
    const address = this.analysis()?.address;
    if (!address) return;
    const query = `${address.street} ${address.houseNumber}, ${address.postalCode} ${address.city}`;
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
      '_blank',
      'noopener,noreferrer',
    );
  }

  onMapCoordinatesChanged(event: {
    latitude: number;
    longitude: number;
  }): Promise<void> {
    return this.updateFacade.onMapCoordinatesChanged(event);
  }

  onSelectedPlanningIdChange(planningId: string): void {
    this.selectedPlanningId.set(planningId);
  }

  private readonly confirmationDialog = inject(ConfirmationDialogService);
  readonly deletingPlanning = signal(false);

  async onDeletePlanning(planningId: string): Promise<void> {
    if (this.deletingPlanning()) return;
    this.deletingPlanning.set(true);
    const confirmed = await this.confirmationDialog.confirm({
      title: 'Planung löschen',
      description: 'Möchten Sie diese Planung löschen? Die Dachanalyse bleibt erhalten.',
      confirmText: 'Planung löschen', cancelText: 'Abbrechen', type: 'warning',
    });
    if (!confirmed) { this.deletingPlanning.set(false); return; }

    const orgId = this.organizationId();
    const analysisId = this.analysisId();
    if (!orgId || !analysisId) { this.deletingPlanning.set(false); return; }

    await this.analysisDetailStore
      .deletePlanning({ organizationId: orgId, analysisId, planningId })
      .then((refreshed) => {
        this.selectedPlanningId.set(this.primaryPlanningId());
        if (refreshed) this.toastService.success('Planung gelöscht');
        else this.toastService.warning('Planung gelöscht. Der aktuelle Analysestatus konnte nicht geladen werden. Bitte laden Sie die Seite erneut.');
      })
      .catch(() => {
        this.toastService.error('Planung konnte nicht gelöscht werden');
      }).finally(() => this.deletingPlanning.set(false));
  }

  onSetPrimaryPlanning(planningId: string): void {
    const orgId = this.organizationId();
    const analysisId = this.analysisId();
    if (!orgId || !analysisId) return;

    this.analysisDetailStore
      .setPrimaryPlanning({ organizationId: orgId, analysisId, planningId })
      .then(() => {
        this.toastService.success('Planung als primär markiert');
      })
      .catch(() => {
        this.toastService.error('Konnte nicht als primär markiert werden');
      });
  }

  onCreatePlanning(): Promise<void> {
    return this.onTimelineActionExecuted({
      action: {
        action: 'start-planning',
        targetStatus: 'planning',
        label: 'Planung erstellen',
        hasPermission: true,
      },
      startTask: 4,
      endTask: 5,
      trigger: 'start',
    });
  }

  onRetryPlanning(planningId: string): Promise<void> {
    return this.workflowFacade.retryPlanning(planningId);
  }

  reloadFilesOverview(): void {
    const organizationId = this.organizationId();
    const analysisId = this.analysisId();
    if (!organizationId || !analysisId) return;
    this.analysisDetailStore.loadFilesOverview({
      organizationId,
      analysisId,
      forceRefresh: true,
    });
  }

  onDeviationPreferenceChanged(ignore: boolean): void {
    this.analysisDetailStore.updateAnalysisOptimistically({ ignoreOffPlaneAreas: ignore });
    void this.updateFacade.markPlanningInputsChanged();
  }

  reloadPolygons(): void {
    const orgId = this.organizationId();
    const analysisId = this.analysisId();
    if (orgId && analysisId) {
      void this.updateFacade.markPlanningInputsChanged();
      this.analysisDetailStore.reloadPolygonsAsync(orgId);
      this.analysisDetailStore.reloadCurrentAnalysis(orgId);
    }
  }

  openDownload(file: FileOverviewItem): void {
    if (!this.currentOrgStore.can('analysis', 'viewDetails')) return;
    if (!file.downloadUrl) {
      this.toastService.warning('Keine Download-URL verfügbar');
      return;
    }
    window.open(file.downloadUrl, '_blank', 'noopener,noreferrer');
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  loadAnalysis(): void {
    const analysisId = this.analysisId();
    const orgId = this.organizationId();

    if (!analysisId || !orgId) {
      debugLog('[AnalysisDetail] Missing analysisId or organizationId');
      return;
    }

    this.analysisDetailStore.initializeAnalysisDetail({
      organizationId: orgId,
      analysisId,
    });

    // Load polygons eagerly so the overview tab can show the Dachanalyse sub-view.
    // The roof tab still force-refreshes on every visit via selectTab().
    this.analysisDetailStore.loadPolygons({
      organizationId: orgId,
      analysisId,
    });
  }

  private async loadCampaignTitle(
    organizationId: string | null,
    campaignId: string | null,
  ): Promise<void> {
    if (!organizationId || !campaignId) {
      this.campaignLookupKey = null;
      this.campaignTitle.set(null);
      return;
    }

    const lookupKey = `${organizationId}:${campaignId}`;
    if (this.campaignLookupKey === lookupKey) return;

    this.campaignLookupKey = lookupKey;
    this.campaignTitle.set(null);
    try {
      const campaign = await this.campaignsService.getCampaignById(
        organizationId,
        campaignId,
      );
      if (this.campaignLookupKey === lookupKey) {
        this.campaignTitle.set(campaign.title);
      }
    } catch {
      if (this.campaignLookupKey === lookupKey) {
        this.campaignTitle.set(null);
      }
    }
  }

  private campaignHref(campaignId: string): string | undefined {
    const slug = this.currentOrgStore.organizationSlug();
    return slug ? `/organizations/${slug}/campaigns/${campaignId}` : undefined;
  }

  private formatHeaderDate(date: Date | string, includeTime = false): string {
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '-';
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: includeTime ? undefined : 'numeric',
      hour: includeTime ? '2-digit' : undefined,
      minute: includeTime ? '2-digit' : undefined,
    }).format(parsed);
  }

  private sourceLabel(source: string): string {
    const labels: Record<string, string> = {
      campaign: 'Kampagne',
      hub: 'Hub',
      plugin: 'Plugin',
      api: 'API',
    };
    return labels[source] ?? source;
  }
}
